/**
 * Field validation and truncation utilities
 * Ensures data conforms to database constraints before submission
 */

/**
 * Field length limits matching database schema constraints
 */
export const FIELD_LIMITS = {
    books: {
        synopsis: 700,
        description: 700,
        notes: 300,
        category: 50,
        category_max_items: 5, // Max 5 categories per book
        language: 30,
        publisher: 50,
        location: 100,
        series: 100,
        author_max_length: 100, // Max length per author name
        author_max_items: 10, // Max 10 authors per book
    },
    readingLists: {
        name: 50,
    },
    profiles: {
        email: 100,
        full_name: 50,
        bio: 300,
    },
} as const

/**
 * Truncates a string value to a specified limit.
 */
export function truncateField(value: string | null | undefined, limit: number): string | null | undefined {
    if (!value || typeof value !== 'string') return value
    if (value.length <= limit) return value
    return value.substring(0, limit)
}

/**
 * Truncates all fields in an object based on a schema of limits.
 */
export function truncateObjectFields<T extends Record<string, unknown>>(
    data: T,
    limits: Record<string, number>
): T {
    const result = { ...data }

    Object.keys(limits).forEach((field) => {
        const value = Object.hasOwn(data, field) ? data[field] : undefined
        if (value !== undefined && value !== null && typeof value === 'string') {
            // @ts-expect-error - Dynamic field assignment is safe here
            result[field] = truncateField(value, limits[field])
        }
    })

    return result
}

/**
 * Validates and sanitizes an array of author names.
 * @returns Validated array of authors (max 10, each max 100 chars).
 */
export function validateAuthors(authors: unknown): string[] {
    if (!Array.isArray(authors)) return []

    return authors
        .filter((author): author is string => author && typeof author === 'string' && !!author.trim())
        .map((author) => truncateField(author.trim(), FIELD_LIMITS.books.author_max_length) || '')
        .slice(0, FIELD_LIMITS.books.author_max_items)
}

/**
 * Validates and sanitizes an array of categories.
 * @returns Validated array of categories (max 5, each max 50 chars).
 */
export function validateCategories(categories: unknown): string[] {
    if (!Array.isArray(categories)) return []

    return categories
        .filter((cat): cat is string => cat && typeof cat === 'string' && !!cat.trim())
        .map((cat) => truncateField(cat.trim(), FIELD_LIMITS.books.category) || '')
        .slice(0, FIELD_LIMITS.books.category_max_items)
}

/**
 * Normalizes value to array format. Handles both string and array inputs.
 */
export function normalizeToArray(value: string | string[] | null | undefined): string[] {
    if (!value) return []
    if (Array.isArray(value)) return value
    if (typeof value === 'string') {
        // Split by comma and filter empty strings
        return value
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
    }
    return []
}
